import { UTM_PARAM_KEYS, UTM_SESSION_KEY } from '@common/auth/constant';
import { resumeSignupHandoff } from '@common/metrics/journey/journeys/signup';
import { Public_Routes } from '@common/url';
import { AlertTriangleIcon, ClockRefreshIcon, LogoProphecyWithNameIcon } from '@prophecy/icons';
import { BaseColor, theme } from '@prophecy/theme';
import { Alert, Avatar, Button, Display, Separator, Spinner, Stack, Text } from '@prophecy/ui-v3';
import { FeaturedIcon } from '@prophecy/ui-v3/Misc/FeaturedIcon';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { csrf } from '../../utils/csrf';
import { hostUrl } from '../../utils/getServerUrl';

const PREVIEW_PAYLOAD: ConsentPayload = {
  request: 'preview-request-id',
  client_id: 'mcp-preview-client',
  client_name: 'Claude',
  display_name: 'Claude',
  user_email: 'you@prophecy.io',
  scopes: ['mcp', 'offline_access', 'openid'],
  all_or_nothing: true,
  verified: false,
  redirect_host: 'claude.ai',
  already_granted: false
};

interface ConsentPayload {
  request: string;
  client_id: string;
  client_name: string;
  display_name: string;
  user_email: string;
  scopes: string[];
  all_or_nothing: boolean;
  verified: boolean;
  redirect_host: string;
  already_granted: boolean;
}

// TODO: confirm refresh-token lifetime with backend and update.
const REFRESH_ACCESS_DAYS = 30;

type ScopeCopy = { label: string; detail?: string };

const SCOPE_COPY: Record<string, ScopeCopy[]> = {
  openid: [
    {
      label: 'Connect to your Prophecy account',
      detail: 'Name and email only.'
    }
  ],
  offline_access: [
    {
      label: 'Stay signed in between chat sessions',
      detail: `Refresh access for up to ${REFRESH_ACCESS_DAYS} days.`
    }
  ],
  mcp: [
    {
      label: 'Manage & store data files and connections',
      detail: 'Create, read, update, and delete data assets in your Databox.'
    },
    {
      label: 'Transform & restructure data',
      detail: 'Run, view, and edit auditable Prophecy data pipelines.'
    },
    {
      label: 'Analyze your data with Prophecy AI',
      detail: 'Generate analysis based on understanding your data.'
    }
  ]
};

function copyForScope(id: string): ScopeCopy[] {
  return SCOPE_COPY[id] ?? [{ label: id }];
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'loaded'; data: ConsentPayload }
  | { kind: 'expired' }
  | { kind: 'network_error'; message: string }
  | { kind: 'unauth' };

const CONSENT_REQUEST_ENDPOINT = `${hostUrl}/api/oauth2/consent/request`;
const CONSENT_DECISION_ENDPOINT = `${hostUrl}/api/oauth2/consent`;

// Sign-up reads UTMs from its own URL or this session key; past the redirect below they would only
// survive nested inside `redirect`.
function storeUtmParams(searchParams: URLSearchParams): void {
  const utmParams = Object.fromEntries(
    UTM_PARAM_KEYS.flatMap((key) => {
      const value = searchParams.get(key);
      return value ? [[key, value]] : [];
    })
  );
  if (Object.keys(utmParams).length) sessionStorage.setItem(UTM_SESSION_KEY, JSON.stringify(utmParams));
}

function signinRedirect(searchParams: URLSearchParams): void {
  storeUtmParams(searchParams);
  // Absolute URL so useRedirectAfterAuth's isSameHost guard (new URL) can parse it and route back
  // to the consent page after login instead of dropping the user on Home.
  window.location.href = Public_Routes.SignIn.getUrlWithBasePath(undefined, { redirect: window.location.href });
}

function authHeaders(includeContentType: boolean): Record<string, string> {
  const h: Record<string, string> = {
    Accept: 'application/json',
    [csrf.key]: csrf.get()
  };
  if (includeContentType) h['Content-Type'] = 'application/json';
  return h;
}

async function fetchConsent(requestId: string, signal: AbortSignal): Promise<LoadState> {
  try {
    const r = await fetch(`${CONSENT_REQUEST_ENDPOINT}/${encodeURIComponent(requestId)}`, {
      credentials: 'include',
      headers: authHeaders(false),
      signal
    });
    if (r.status === 401) return { kind: 'unauth' };
    if (r.status === 404 || r.status === 410) return { kind: 'expired' };
    if (!r.ok) return { kind: 'network_error', message: `Request failed (${r.status})` };
    const data = (await r.json()) as ConsentPayload;
    return { kind: 'loaded', data };
  } catch (e) {
    if ((e as { name?: string }).name === 'AbortError') return { kind: 'loading' };
    return { kind: 'network_error', message: (e as Error).message };
  }
}

async function isConsumedRequest(r: Response): Promise<boolean> {
  const body = (await r.json().catch(() => null)) as { error?: string } | null;
  return body?.error === 'invalid_request';
}

export default function OAuth2Consent() {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request') ?? '';
  const isPreview = searchParams.get('preview') === '1';
  const [state, setState] = useState<LoadState>(
    isPreview ? { kind: 'loaded', data: PREVIEW_PAYLOAD } : { kind: 'loading' }
  );
  const [inFlight, setInFlight] = useState<'allow' | 'deny' | null>(null);
  const [postError, setPostError] = useState<string | null>(null);

  // A DataGPT signup lands here instead of onboarding, so the signup journey completes here too.
  useEffect(() => {
    resumeSignupHandoff();
  }, []);

  useEffect(() => {
    if (isPreview) return;
    if (!requestId) {
      setState({ kind: 'expired' });
      return;
    }
    const ctrl = new AbortController();
    void fetchConsent(requestId, ctrl.signal).then((next) => {
      if (!ctrl.signal.aborted) setState(next);
    });
    return () => ctrl.abort();
  }, [requestId, isPreview]);

  useEffect(() => {
    if (state.kind === 'unauth') signinRedirect(searchParams);
  }, [state.kind, searchParams]);

  const post = async (kind: 'allow' | 'deny'): Promise<void> => {
    setInFlight(kind);
    setPostError(null);
    try {
      const r = await fetch(CONSENT_DECISION_ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        redirect: 'follow',
        headers: authHeaders(true),
        body: JSON.stringify({ request: requestId, decision: kind })
      });
      if (r.status === 401) {
        signinRedirect(searchParams);
        return;
      }
      if (r.status === 410) {
        setState({ kind: 'expired' });
        return;
      }
      if (r.redirected) {
        window.location.href = r.url;
        return;
      }
      // The backend consumes a request on its first decision, so a retry with the same id can never succeed.
      if (r.status === 400 && (await isConsumedRequest(r))) {
        setState({ kind: 'expired' });
        return;
      }
      if (!r.ok) {
        setPostError(`Request failed (${r.status}). Try again.`);
        return;
      }
      const j = (await r.json().catch(() => ({}))) as { redirect_url?: string; redirect_uri?: string };
      const target = j.redirect_url ?? j.redirect_uri;
      if (target) window.location.href = target;
    } catch (e) {
      setPostError((e as Error).message);
    } finally {
      setInFlight(null);
    }
  };

  const onSwitchAccount = async (): Promise<void> => {
    try {
      await fetch(`${hostUrl}/api/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(true),
        body: '{}'
      });
    } catch {
      // Even if logout fails, still route to sign-in so the user isn't stuck.
    }
    signinRedirect(searchParams);
  };

  return (
    <Page>
      <BrandMark>
        <LogoProphecyWithNameIcon width={180} height={40} />
      </BrandMark>
      <Card>{renderBody(state, inFlight, postError, post, onSwitchAccount)}</Card>
    </Page>
  );
}

function renderBody(
  state: LoadState,
  inFlight: 'allow' | 'deny' | null,
  postError: string | null,
  post: (kind: 'allow' | 'deny') => Promise<void>,
  onSwitchAccount: () => void
) {
  if (state.kind === 'loading' || state.kind === 'unauth') {
    return (
      <CenteredMuted>
        <Spinner spinning={true} />
      </CenteredMuted>
    );
  }
  if (state.kind === 'expired') {
    return (
      <EmptyState
        icon={<ClockRefreshIcon type='default' />}
        tone={BaseColor.warning}
        title='This authorization request expired'
        detail='Sign-in requests are valid for a few minutes. Return to the app that started sign-in and try again.'
      />
    );
  }
  if (state.kind === 'network_error') {
    return (
      <EmptyState
        icon={<AlertTriangleIcon type='default' />}
        tone={BaseColor.error}
        title='Something went wrong'
        detail={state.message}
        cta={
          <Button
            variant='primary'
            aria-label='Try again'
            data-test-id='oauth2-consent-retry'
            onClick={() => window.location.reload()}>
            Try again
          </Button>
        }
      />
    );
  }
  return (
    <ConsentCard
      data={state.data}
      inFlight={inFlight}
      postError={postError}
      onAllow={() => void post('allow')}
      onDeny={() => void post('deny')}
      onSwitchAccount={onSwitchAccount}
    />
  );
}

function ConsentCard({
  data,
  inFlight,
  postError,
  onAllow,
  onDeny,
  onSwitchAccount
}: {
  data: ConsentPayload;
  inFlight: 'allow' | 'deny' | null;
  postError: string | null;
  onAllow: () => void;
  onDeny: () => void;
  onSwitchAccount: () => void;
}) {
  const displayName = data.display_name || data.client_name;
  const busy = inFlight != null;

  return (
    <Stack gap={theme.spaces.x24}>
      <TitleBlock>
        <Display level='sm'>{displayName} wants to access your Prophecy account</Display>
      </TitleBlock>

      {data.already_granted && (
        <Alert variant={'info'}>
          <Text level='sm'>
            You have previously authorized {displayName}. Allowing again renews access with the scopes listed below.
          </Text>
        </Alert>
      )}

      <Separator />

      <Stack gap={theme.spaces.x12}>
        <Text level='sm' tone={theme.colors.gray800}>
          This will let {displayName}:
        </Text>
        <Stack gap={theme.spaces.x8}>
          {data.scopes.flatMap((id) =>
            copyForScope(id).map((copy, idx) => (
              <ScopeRow key={`${id}:${idx}`}>
                <ScopeCheck>✓</ScopeCheck>
                <Stack gap={theme.spaces.x4}>
                  <Text level='sm'>{copy.label}</Text>
                  {copy.detail && (
                    <Text level='xs' tone={theme.colors.gray500}>
                      {copy.detail}
                    </Text>
                  )}
                </Stack>
              </ScopeRow>
            ))
          )}
        </Stack>
        {data.all_or_nothing && (
          <Text level='xs' tone={theme.colors.gray500}>
            All permissions must be granted together — individual scopes can&apos;t be turned off.
          </Text>
        )}
      </Stack>

      <Separator />

      <Stack gap={theme.spaces.x8}>
        <Text level='xs' tone={theme.colors.gray500}>
          Signed in as
        </Text>
        <UserRow>
          <Avatar size='m' text={emailInitials(data.user_email)} />
          <Stack gap={theme.spaces.x2}>
            <Text level='sm'>{data.user_email}</Text>
            <SwitchAccountLink
              type='button'
              onClick={onSwitchAccount}
              disabled={busy}
              aria-label='Switch account'
              data-test-id='oauth2-consent-switch-account'>
              Not you? Switch account
            </SwitchAccountLink>
          </Stack>
        </UserRow>
      </Stack>

      <Separator />

      <Stack gap={theme.spaces.x4}>
        <Text level='xs' tone={theme.colors.gray500}>
          Redirecting to
        </Text>
        <Text level='sm'>{data.redirect_host}</Text>
      </Stack>

      {postError && <Alert variant={'error'} details={postError} />}

      <Stack direction='horizontal' gap={theme.spaces.x8} align='space-between'>
        <Button
          aria-label='Cancel authorization'
          data-test-id='oauth2-consent-deny'
          variant='secondary'
          onClick={onDeny}
          disabled={busy}>
          {inFlight === 'deny' ? 'Cancelling…' : 'Cancel'}
        </Button>
        <Button
          aria-label={`Allow ${displayName} access`}
          data-test-id='oauth2-consent-allow'
          variant='primary'
          onClick={onAllow}
          disabled={busy}>
          {inFlight === 'allow' ? 'Authorizing…' : 'Allow access'}
        </Button>
      </Stack>

      <FooterNote>
        <Text level='xs' tone={theme.colors.gray500}>
          You can revoke access anytime in Settings → Authorized applications.
        </Text>
      </FooterNote>
    </Stack>
  );
}

function emailInitials(email: string): string {
  return (
    email
      .split(/[\s@]/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join('') || '?'
  );
}

function EmptyState({
  icon,
  tone,
  title,
  detail,
  cta
}: {
  icon: React.ReactNode;
  tone: BaseColor;
  title: string;
  detail: string;
  cta?: React.ReactNode;
}) {
  return (
    <EmptyStack>
      <FeaturedIcon icon={icon} tone={tone} size='xl' variant='lightGradient' />
      <Stack gap={theme.spaces.x8} align='center'>
        <Display level='sm'>{title}</Display>
        <Text level='sm' tone={theme.colors.gray600}>
          {detail}
        </Text>
      </Stack>
      {cta}
    </EmptyStack>
  );
}

/**
 * `#root` is already exactly 100vh and a column flex container, so asking for 100vh here clamped
 * the background to the window while the card scrolled past it onto white. The page takes the
 * window and scrolls inside it instead; auto margins centre the card without `justify-content`,
 * which puts the top of an overflowing column above the scroll origin and out of reach.
 */
const Page = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: ${theme.colors.gray50};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spaces.x24};
  padding: ${theme.spaces.x32} ${theme.spaces.x16};

  > *:first-child {
    margin-top: auto;
  }
  > *:last-child {
    margin-bottom: auto;
  }
`;

const BrandMark = styled.div`
  display: flex;
  justify-content: center;
  svg {
    height: 40px;
    width: auto;
  }
`;

const Card = styled.section`
  width: 480px;
  max-width: 100%;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.gray200};
  border-radius: ${theme.radius.x8};
  padding: ${theme.spaces.x32};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spaces.x8};
  text-align: center;
`;

const FooterNote = styled.div`
  text-align: center;
`;

const CenteredMuted = styled.div`
  display: flex;
  justify-content: center;
  padding: ${theme.spaces.x48} 0;
`;

const EmptyStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spaces.x20};
  text-align: center;
  padding: ${theme.spaces.x16} 0;
`;

const ScopeRow = styled.div`
  display: flex;
  gap: ${theme.spaces.x8};
  align-items: flex-start;
`;

const ScopeCheck = styled.span`
  color: ${theme.colors.success600};
  font-weight: 600;
`;

const UserRow = styled.div`
  display: flex;
  gap: ${theme.spaces.x12};
  align-items: center;
`;

const SwitchAccountLink = styled.button`
  align-self: flex-start;
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  font-size: ${theme.fontSizes.x12};
  line-height: 1;
  color: ${theme.colors.brand600};
  cursor: pointer;
  text-align: left;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }
  &:disabled {
    color: ${theme.colors.gray400};
    cursor: not-allowed;
  }
`;
